/**
 * /picks/edit — screen 3q.
 *
 * The week's whole slate in one scrollable list, grouped by kickoff slot. It
 * used to live at `/picks`; the board that lists everyone took that URL, and
 * this is now what the "Add or edit picks" button opens.
 *
 *   D78 — a pick locks at ITS OWN game's kickoff, never at a weekly deadline,
 *         and every tap saves immediately. There is no submit button, because
 *         there is nothing to submit: the server already has it.
 *   D80 — nothing is ever auto-picked on someone's behalf. "Pick all favorites"
 *         is a deliberate tap and only fills games that are unlocked AND
 *         unpicked (the server enforces that half).
 *   D84 — individual picks stay hidden from everyone until each game starts,
 *         which is what the closing line on this screen promises.
 *
 * A 409 `locked` means the game kicked off between render and tap. That is not
 * an error the user did anything about, so it raises a toast and refetches
 * rather than shouting.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { PickGame, PicksWeek } from '@shared/types'
import { teamAbbr } from '@shared/teams'
import { useStore } from '../lib/store'
import { ApiFailure, GET, POST, PUT } from '../lib/api'
import { Body, Eyebrow, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'

const WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: 'long' })
const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })

/** "1:00" — the bare clock used by the per-group lock line. */
function clockOnly(at: number): string {
  const d = new Date(at)
  const h = d.getHours() % 12 || 12
  return `${h}:${String(d.getMinutes()).padStart(2, '0')}`
}

interface Slot {
  key: string
  at: number
  locked: boolean
  games: PickGame[]
}

/** Group by local day + kickoff time, which is how a slate is actually read. */
function toSlots(games: PickGame[], now: number): Slot[] {
  const out: Slot[] = []
  for (const pg of [...games].sort((a, b) => a.game.kickoffAt - b.game.kickoffAt)) {
    const d = new Date(pg.game.kickoffAt)
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`
    const last = out[out.length - 1]
    if (last && last.key === key) last.games.push(pg)
    else out.push({ key, at: pg.game.kickoffAt, locked: false, games: [pg] })
  }
  for (const s of out) s.locked = s.games.every((g) => g.locked || now >= g.game.kickoffAt)
  return out
}

export default function PicksEdit() {
  const { toast, patch } = useStore()
  const navigate = useNavigate()
  const [week, setWeek] = useState<PicksWeek | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [autoBusy, setAutoBusy] = useState(false)
  const [tiebreak, setTiebreak] = useState('')
  const tiebreakTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await GET<PicksWeek>('/api/picks', signal)
      setWeek(res)
      setTiebreak(res.tiebreakerValue === null ? '' : String(res.tiebreakerValue))
      setErr(null)
      return res
    } catch (e) {
      if (signal?.aborted) return null
      // Keep the last-known-good board on screen; the notice explains the gap.
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load this week’s picks.')
      return null
    }
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  useEffect(() => () => { if (tiebreakTimer.current) clearTimeout(tiebreakTimer.current) }, [])

  const now = Date.now()
  const slots = useMemo(() => (week ? toSlots(week.games, now) : []), [week, now])

  // ── one tap = one save (D78) ──────────────────────────────────────────────

  const pick = useCallback(async (gameId: string, teamId: string) => {
    setBusy(gameId)
    // Optimistic: the tap has to feel instant, and the rollback is cheap.
    setWeek((w) => {
      if (!w) return w
      const games = w.games.map((g) => (g.game.id === gameId ? { ...g, myPick: teamId } : g))
      const made = games.filter((g) => g.myPick !== null).length
      return { ...w, games, made }
    })
    try {
      const res = await PUT<{ gameId: string; teamId: string; made: number; total: number }>(
        `/api/picks/${encodeURIComponent(gameId)}`, { teamId },
      )
      setWeek((w) => (w ? { ...w, made: res.made, total: res.total } : w))
      // Home reads `state.picks`, which this screen does not own. Without this
      // the counter there stays at whatever it was when the app loaded.
      patch((prev) => ({ ...prev, picks: { ...prev.picks, made: res.made, total: res.total } }))
      setErr(null)
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'locked') {
        toast('That game just kicked off. The pick is locked.')
        void load()
      } else {
        toast(e instanceof ApiFailure ? e.message : 'That pick didn’t save.', {
          label: 'Retry', run: () => { void pick(gameId, teamId) },
        })
        void load()
      }
    } finally {
      setBusy(null)
    }
  }, [load, toast])

  const pickAllFavorites = useCallback(async () => {
    setAutoBusy(true)
    try {
      const res = await POST<{ filled: number }>('/api/picks/auto', { strategy: 'favorites' })
      const filled = await load()
      if (filled) patch((prev) => ({ ...prev, picks: { ...prev.picks, made: filled.made, total: filled.total } }))
      toast(res.filled === 0
        ? 'Nothing left to fill. Every open game already has a pick.'
        : `Filled ${res.filled} pick${res.filled === 1 ? '' : 's'} with the favorite.`)
    } catch (e) {
      toast(e instanceof ApiFailure ? e.message : 'Couldn’t fill the favorites.')
    } finally {
      setAutoBusy(false)
    }
  }, [load, toast])

  // ── tiebreaker (D79) ──────────────────────────────────────────────────────

  const saveTiebreak = useCallback(async (weekId: string, raw: string) => {
    const n = Number(raw)
    if (raw.trim() === '' || !Number.isInteger(n) || n < 0 || n > 200) return
    try {
      await PUT(`/api/picks/${encodeURIComponent(weekId)}/tiebreaker`, { totalPts: n })
      setErr(null)
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'locked') {
        toast('The tiebreaker game has kicked off.')
        void load()
        return
      }
      toast(e instanceof ApiFailure ? e.message : 'That number didn’t save.')
    }
  }, [load, toast])

  const onTiebreakChange = useCallback((value: string) => {
    const clean = value.replace(/[^\d]/g, '').slice(0, 3)
    setTiebreak(clean)
    if (tiebreakTimer.current) clearTimeout(tiebreakTimer.current)
    const weekId = week?.weekId
    if (!weekId) return
    tiebreakTimer.current = setTimeout(() => { void saveTiebreak(weekId, clean) }, 600)
  }, [saveTiebreak, week?.weekId])

  const tiebreakGame = week?.games.find((g) => g.game.id === week.tiebreakerGameId) ?? null
  const tiebreakLabel = tiebreakGame
    ? `${teamAbbr(tiebreakGame.game.awayTeamId)} @ ${teamAbbr(tiebreakGame.game.homeTeamId)}`
    : null
  const tiebreakLocked = tiebreakGame ? tiebreakGame.locked || now >= tiebreakGame.game.kickoffAt : true

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>
          🎯 {week ? `${week.weekLabel} Picks` : 'Picks'}
        </span>}
        right={
          <span className="mono" style={{ fontSize: 12, color: 'var(--dim)' }}>
            {week ? `${week.made} of ${week.total}` : '-'}
          </span>
        }
        sub={
          <button
            onClick={() => { void pickAllFavorites() }}
            disabled={autoBusy || !week}
            style={{
              display: 'block', width: '100%', textAlign: 'center',
              marginTop: 6, padding: 11, minHeight: 44, borderRadius: 12,
              border: '1px solid var(--line)', fontSize: 13, fontWeight: 600,
              color: 'var(--bright)', opacity: autoBusy || !week ? 0.6 : 1,
            }}
          >
            {autoBusy ? 'Filling…' : 'Pick all favorites'}
          </button>
        }
      />

      <Body style={{ padding: '6px var(--gutter) calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!week ? (
          <>
            <SkeletonCard lines={3} />
            <Spacer h={12} />
            <SkeletonCard lines={4} />
            <Spacer h={12} />
            <SkeletonCard lines={4} />
          </>
        ) : week.games.length === 0 ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>No games this week.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              The slate shows up as soon as the schedule syncs.
            </div>
          </div>
        ) : (
          <>
            {slots.map((slot, i) => (
              <div key={slot.key}>
                <Eyebrow style={{ marginTop: i === 0 ? 0 : 20 }}>
                  {slot.locked
                    ? `${WEEKDAY.format(slot.at)}, locked`
                    : `${WEEKDAY.format(slot.at)} ${HHMM.format(slot.at)}`}
                </Eyebrow>
                {slot.games.map((pg) => (
                  pg.locked || now >= pg.game.kickoffAt
                    ? <LockedRow key={pg.game.id} pg={pg} />
                    : (
                      <OpenRow
                        key={pg.game.id}
                        pg={pg}
                        busy={busy === pg.game.id}
                        onPick={(teamId) => { void pick(pg.game.id, teamId) }}
                      />
                    )
                ))}
                {!slot.locked ? (
                  <div className="mono" style={{ fontSize: 10, color: 'var(--dim)', marginTop: 8 }}>
                    locks {clockOnly(slot.at)}
                  </div>
                ) : null}
              </div>
            ))}

            {tiebreakLabel ? (
              <>
                <Eyebrow style={{ marginTop: 22 }}>Tiebreaker</Eyebrow>
                <label
                  htmlFor="tiebreaker"
                  style={{ display: 'block', fontSize: 13, color: 'var(--dim)', marginTop: 8 }}
                >
                  Total combined points in {tiebreakLabel}
                </label>
                <input
                  id="tiebreaker"
                  className="mono"
                  value={tiebreak}
                  onChange={(e) => onTiebreakChange(e.target.value)}
                  onBlur={() => { if (week) void saveTiebreak(week.weekId, tiebreak) }}
                  disabled={tiebreakLocked}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  aria-label={`Total combined points in ${tiebreakLabel}`}
                  placeholder="-"
                  style={{
                    marginTop: 10, padding: '13px 16px', borderRadius: 12,
                    border: '1px solid var(--line)', background: 'var(--surf)',
                    fontSize: 16, width: 90, minHeight: 48, textAlign: 'center',
                    color: 'var(--fg)', opacity: tiebreakLocked ? 0.6 : 1,
                  }}
                />
                {tiebreakLocked ? (
                  <div className="mono" style={{ fontSize: 10, color: 'var(--dim)', marginTop: 8 }}>
                    locked, that game has kicked off
                  </div>
                ) : null}
              </>
            ) : null}

            <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 18, lineHeight: 1.5 }}>
              Your picks are hidden from everyone until each game starts.
            </div>

            <div style={{ marginTop: 18, display: 'flex', gap: 16 }}>
              <Link to="/picks" style={{ fontSize: 13, fontWeight: 600, color: 'var(--bright)' }}>
                The board →
              </Link>
              <Link to="/picks/standings" style={{ fontSize: 13, fontWeight: 600, color: 'var(--bright)' }}>
                Standings →
              </Link>
            </div>

            {/* Every pick is saved the moment it is tapped, so there is nothing
                to submit and no reason to gate the way out on a full card. The
                button is a door, not a commit: leave with four of sixteen and
                the other twelve are simply still open. */}
            <button
              onClick={() => navigate('/picks')}
              style={{
                display: 'block', width: '100%', marginTop: 26, padding: 15, minHeight: 48,
                borderRadius: 'var(--radius-btn)', background: 'var(--fill)', color: 'var(--ink)',
                fontSize: 15, fontWeight: 700, textAlign: 'center',
              }}
            >
              {week && week.made < week.total ? `Finish later (${week.made} of ${week.total})` : 'Finish'}
            </button>
          </>
        )}
      </Body>
    </Screen>
  )
}

/** A game that has kicked off: one mono row, result on the left, verdict right. */
function LockedRow({ pg }: { pg: PickGame }) {
  const g = pg.game
  const away = teamAbbr(g.awayTeamId)
  const home = teamAbbr(g.homeTeamId)
  const scored = g.awayScore !== null && g.homeScore !== null
  const left = scored ? `${away} ${g.awayScore} @ ${home} ${g.homeScore}` : `${away} @ ${home}`

  let right: string
  let color: string
  if (!pg.myPick) {
    right = 'no pick'
    color = 'var(--dim)'
  } else if (pg.correct === true) {
    right = `✓ you picked ${teamAbbr(pg.myPick)}`
    color = 'var(--bright)'
  } else if (pg.correct === false) {
    right = `✗ you picked ${teamAbbr(pg.myPick)}`
    color = 'var(--alert-fg)'
  } else {
    right = `you picked ${teamAbbr(pg.myPick)}`
    color = 'var(--dim)'
  }

  return (
    <div
      className="mono"
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
        padding: '12px 0', borderBottom: '1px solid var(--line)', fontSize: 13, minHeight: 44,
      }}
    >
      <span style={{ minWidth: 0 }}>{left}</span>
      <span style={{ color, flex: '0 0 auto' }}>{right}</span>
    </div>
  )
}

/** An open game: two flexed buttons. The picked side is `fill`/`ink`. */
function OpenRow({
  pg, busy, onPick,
}: { pg: PickGame; busy: boolean; onPick: (teamId: string) => void }) {
  const g = pg.game
  const sides = [
    { teamId: g.awayTeamId, abbr: teamAbbr(g.awayTeamId) },
    { teamId: g.homeTeamId, abbr: teamAbbr(g.homeTeamId) },
  ]
  const label = `${teamAbbr(g.awayTeamId)} at ${teamAbbr(g.homeTeamId)}`

  return (
    <div
      role="group"
      aria-label={label}
      style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, opacity: busy ? 0.7 : 1 }}
    >
      {sides.map((s) => {
        const on = pg.myPick === s.teamId
        const anyPick = pg.myPick !== null
        return (
          <button
            key={s.teamId}
            onClick={() => { if (!on) onPick(s.teamId) }}
            aria-pressed={on}
            aria-label={`Pick ${s.abbr} in ${label}`}
            style={{
              flex: 1, textAlign: 'center', padding: 14, minHeight: 48, borderRadius: 12,
              fontSize: 14,
              fontWeight: on ? 700 : 600,
              background: on ? 'var(--fill)' : 'transparent',
              color: on ? 'var(--ink)' : anyPick ? 'var(--fg)' : 'var(--dim)',
              border: on ? '1px solid var(--fill)' : '1px solid var(--line)',
            }}
          >
            {s.abbr}
          </button>
        )
      })}
    </div>
  )
}
